import { useEffect, useState } from "react";
import { axios_tenant } from "../../../../axios";

const BASE_URL = "/collaboration/workflow";

/**
 * Per-user visible-column selection for a task grid (COL-150), stored
 * server-side under `gridKey`. Used by the PGS and OA Shell task lists.
 *
 * `columnPrefs` is null when there is no saved preference, which means every
 * column is shown. Mandatory columns are always shown and never stored.
 *
 * @param {Object}   options
 * @param {string}   options.gridKey          - Preference key, e.g. "pgs-tasks"
 * @param {string[]} options.mandatoryColumns - Column ids that can't be hidden
 * @param {boolean}  options.enabled          - Only Admin/AM have the picker
 * @param {Function} options.onSaveError      - Called with a user-facing message
 */
const useGridColumnPrefs = ({ gridKey, mandatoryColumns, enabled, onSaveError }) => {
    const [columnPrefs, setColumnPrefs] = useState(null);

    // Load the saved selection once per session; any failure (including the
    // table not existing yet) silently falls back to the default column set.
    useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;
        axios_tenant.get(`${BASE_URL}/grid-preferences/${gridKey}`, { silent: true })
            .then(res => {
                if (cancelled) return;
                const saved = res.data?.data?.columns;
                setColumnPrefs(Array.isArray(saved) ? saved : null);
            })
            .catch(() => { if (!cancelled) setColumnPrefs(null); });
        return () => { cancelled = true; };
    }, [enabled, gridKey]);

    const optionalIds = (allColumns) => allColumns.map(c => c.id).filter(id => !mandatoryColumns.includes(id));

    /** Applies the saved selection; users without the picker or a saved selection see every column. */
    const applyColumnPrefs = (allColumns) => {
        if (!enabled || !Array.isArray(columnPrefs)) return allColumns;
        return allColumns.filter(c => mandatoryColumns.includes(c.id) || columnPrefs.includes(c.id));
    };

    const isColumnVisible = (columnId) => !Array.isArray(columnPrefs) || columnPrefs.includes(columnId);

    const saveColumnPrefs = async (nextPrefs) => {
        setColumnPrefs(nextPrefs);
        try {
            await axios_tenant.put(`${BASE_URL}/grid-preferences/${gridKey}`, { columns: nextPrefs }, { silent: true });
        } catch (err) {
            // A 4xx carries a message worth showing (validation, permission); anything
            // else is a server-side problem the user can't act on, so say what it means
            // for them: the view changed, but only for this session.
            const status = err?.response?.status;
            const serverMessage = status && status < 500 ? err?.response?.data?.message : null;
            onSaveError?.(serverMessage || "Column selection could not be saved. Your view is updated for this session only.");
        }
    };

    /** Toggles one column, keeping the stored list in the grid's column order. */
    const toggleColumn = (allColumns, columnId) => {
        const current = Array.isArray(columnPrefs) ? columnPrefs : optionalIds(allColumns);
        const next = current.includes(columnId)
            ? current.filter(id => id !== columnId)
            : optionalIds(allColumns).filter(id => current.includes(id) || id === columnId);
        saveColumnPrefs(next);
    };

    const resetColumns = (allColumns) => saveColumnPrefs(optionalIds(allColumns));

    return { applyColumnPrefs, isColumnVisible, toggleColumn, resetColumns };
};

export default useGridColumnPrefs;
